import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { 
  Flower2, Clock, MessageCircle, Shield, Heart, Brain, Moon, Wind,
  Loader2, CheckCircle, Sparkles, CreditCard, AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AriaPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [ariaInfo, setAriaInfo] = useState(null);
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    if (searchParams.get('cancelled')) {
      toast.error('Paiement annulé');
    }
    fetchAriaInfo();
    fetchAvailability();
    
    // Refresh availability every minute
    const interval = setInterval(fetchAvailability, 60000);
    return () => clearInterval(interval);
  }, [searchParams]);

  const fetchAriaInfo = async () => {
    try {
      const response = await axios.get(`${API}/aria/info`);
      setAriaInfo(response.data);
    } catch (error) {
      console.error('Error fetching Aria info:', error);
    }
  };

  const fetchAvailability = async () => {
    try {
      const response = await axios.get(`${API}/aria/availability`);
      setAvailability(response.data);
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  };

  const handleStartSession = async () => {
    if (!availability?.is_available) {
      toast.error(`Aria n'est pas disponible. Revenez à ${availability?.next_available_hour}h`);
      return;
    }
    
    setLoading(true);
    
    try {
      const payload = {
        origin_url: window.location.origin
      };
      
      if (!isAuthenticated) {
        const email = prompt('Entrez votre adresse email pour commencer :');
        if (!email) {
          setLoading(false);
          return;
        }
        payload.email = email;
      }
      
      const response = await axios.post(
        `${API}/aria/checkout`,
        payload,
        isAuthenticated ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la session');
      setLoading(false);
    }
  };

  const formatHours = (hours) => {
    // hours is now a string like "00h-08h, 12h-14h, 18h-24h"
    if (!hours) return '';
    return hours;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-pink-50 to-white">
      <Navbar />
      
      <div className="container-app py-12">
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            {/* Avatar */}
            <div className="relative inline-block mb-6">
              <div className={`w-32 h-32 bg-gradient-to-br ${availability?.is_available ? 'from-purple-400 to-pink-400' : 'from-gray-400 to-gray-500'} rounded-full flex items-center justify-center shadow-xl`}>
                <Flower2 className="w-16 h-16 text-white" />
              </div>
              <div className={`absolute -bottom-2 -right-2 ${availability?.is_available ? 'bg-green-500' : 'bg-gray-400'} text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 shadow-lg`}>
                <span className={`w-2 h-2 bg-white rounded-full ${availability?.is_available ? 'animate-pulse' : ''}`}></span>
                {availability?.is_available ? 'En ligne' : 'Hors ligne'}
              </div>
            </div>
            
            <h1 className="font-heading text-4xl font-semibold mb-4 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Aria
            </h1>
            <p className="text-xl text-muted-foreground mb-2">
              Votre conseillère santé et bien-être
            </p>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Disponible à certaines heures pour vous accompagner dans la gestion du stress, 
              de l'anxiété et vous proposer des techniques de relaxation personnalisées.
            </p>
            
            {/* Availability Schedule */}
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-purple-100">
              <Clock className="w-4 h-4 text-purple-500" />
              <span className="text-sm text-muted-foreground">
                Aujourd'hui : <span className="font-medium text-purple-700">{formatHours(availability?.available_hours)}</span>
              </span>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            <div className="bg-white rounded-2xl p-5 shadow-soft border border-purple-100">
              <Brain className="w-8 h-8 text-purple-500 mb-3" />
              <h3 className="font-semibold mb-1">Stress & Anxiété</h3>
              <p className="text-sm text-muted-foreground">Techniques de gestion et exercices apaisants</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-soft border border-pink-100">
              <Heart className="w-8 h-8 text-pink-500 mb-3" />
              <h3 className="font-semibold mb-1">Maux de tête</h3>
              <p className="text-sm text-muted-foreground">Conseils et méthodes de soulagement</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-soft border border-indigo-100">
              <Moon className="w-8 h-8 text-indigo-500 mb-3" />
              <h3 className="font-semibold mb-1">Sommeil</h3>
              <p className="text-sm text-muted-foreground">Améliorer la qualité de vos nuits</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-soft border border-teal-100">
              <Wind className="w-8 h-8 text-teal-500 mb-3" />
              <h3 className="font-semibold mb-1">Relaxation</h3>
              <p className="text-sm text-muted-foreground">Respiration et méditation guidée</p>
            </div>
          </div>

          {/* Pricing Card */}
          <div className="bg-white rounded-3xl shadow-xl border border-purple-100 overflow-hidden max-w-lg mx-auto">
            <div className={`bg-gradient-to-r ${availability?.is_available ? 'from-purple-500 to-pink-500' : 'from-gray-400 to-gray-500'} p-6 text-white text-center`}>
              <Sparkles className="w-10 h-10 mx-auto mb-2" />
              <h2 className="text-2xl font-semibold">Session de 30 minutes</h2>
              <p className={availability?.is_available ? 'text-purple-100' : 'text-gray-200'}>Échangez par chat avec Aria</p>
            </div>
            
            <div className="p-6">
              {/* Availability Status */}
              {!availability?.is_available && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
                    <div>
                      <p className="font-medium text-amber-800">Aria n'est pas disponible</p>
                      <p className="text-sm text-amber-600">
                        Prochaine disponibilité : <span className="font-semibold">{availability?.next_available_hour}h</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="text-center mb-6">
                <span className="text-5xl font-bold text-gray-900">30€</span>
                <span className="text-muted-foreground ml-2">/ session</span>
              </div>
              
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-sm">
                  <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                  <span>Chat en temps réel avec Aria</span>
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                  <span>30 minutes de conseils personnalisés</span>
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                  <span>7 heures de disponibilité par jour</span>
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                  <span>Exercices de relaxation guidés</span>
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                  <span>Historique de conversation sauvegardé</span>
                </li>
              </ul>
              
              <Button 
                onClick={handleStartSession}
                disabled={loading || !availability?.is_available}
                className={`w-full h-12 ${availability?.is_available ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600' : 'bg-gray-400 cursor-not-allowed'} text-white text-lg font-medium`}
                data-testid="start-aria-session"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Redirection...
                  </>
                ) : availability?.is_available ? (
                  <>
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Commencer une session
                  </>
                ) : (
                  <>
                    <Clock className="w-5 h-5 mr-2" />
                    Disponible à {availability?.next_available_hour}h
                  </>
                )}
              </Button>
              
              <p className="text-xs text-center text-muted-foreground mt-4 flex items-center justify-center gap-1">
                <CreditCard className="w-3 h-3" />
                Paiement sécurisé par Stripe
              </p>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-4 max-w-lg mx-auto">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Information importante</p>
                <p>
                  Aria est un assistant santé et bien-être qui fournit des conseils généraux. 
                  Elle ne remplace pas une consultation médicale. Pour tout symptôme grave ou persistant, 
                  consultez un professionnel de santé.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AriaPage;
